/**
 * SQLite engine compatibility seam for the public SDK database path.
 *
 * §96's `sqlite3mt/` implementation is intended to replace the legacy
 * `sqlite-worker.ts` path behind `openDatabase()`. Today the public database
 * layer still reaches into legacy worker-specific handle internals
 * (`memoryManager`, `vfs`, WAL reimport helpers, diagnostics views).
 *
 * This module defines the exact compatibility surface that `realSqliteDatabase`
 * currently depends on, and provides the default WorkspaceSession engine-factory
 * wiring that adapts the legacy worker implementation to that surface. The
 * sqlite3mt-side assembly lives in `sqlite3mt/engine-factory.ts`, so future
 * session/workspace integration can swap runtimes without rewriting the public
 * API layer again.
 */

import type { TransactionMode } from './pageChangeTracker'
import type {
    DatabaseConfig,
    SqliteFlushLocalOptions,
    SyncDiag,
    SyncEvent,
    SyncMode,
    SyncResult,
} from './sqliteDatabase'
export {
    createSqlite3MtLifecycleRuntimeBridge,
    createSqlite3MtEngineFactory,
    createSqlite3MtDatabaseAdapter,
    reserveSqlite3MtClientAttachment,
    releaseSqlite3MtClientAttachment,
    dispatchSqlite3MtExternalMessage,
    enterSqlite3MtExternalClientIoFatal,
    enterSqlite3MtExternalClientRequest,
    leaveSqlite3MtExternalClientRequest,
    type CreateSqlite3MtRuntimeArgs,
    type Sqlite3MtClientAttachment,
    type Sqlite3MtLifecycleRuntimeBridgeDeps,
    type Sqlite3MtEngineFactoryDeps,
    type Sqlite3MtHandleContext,
    type Sqlite3MtHandleHooks,
    type Sqlite3MtRuntimeBridge,
    type Sqlite3MtAdapterState,
    type Sqlite3Oo1Database,
    type Sqlite3WasmRuntimeApi,
} from './sqlite3mt/engine-factory'

export interface SqliteDirtyPage {
    pgno: number
    pageData: Uint8Array
}

export interface SqliteVfsCompat {
    readonly vfsName: string
    readonly currentPageSize: number
}

export interface SqliteMemoryManagerCompat {
    readonly sab1: SharedArrayBuffer | ArrayBuffer
    readonly sab2: SharedArrayBuffer | ArrayBuffer
    readonly sab3: SharedArrayBuffer | ArrayBuffer
    readonly isSabMode: boolean
    readonly autoCheckpointThreshold?: number
    readonly pageSize: number
    readonly dbPageCount: number
    readonly walDataSize: number
    readonly pageWindowSlots: number
    readonly pageWindowUsed: number
    readonly dirtyPageCount: number
    readonly lockView: Int32Array

    shmRegion(iRegion: number, regionSize: number): Uint8Array | null
    isMode0Active(): boolean
    isDrainPending(): boolean
    isImportPending(): boolean
}

/**
 * Optional database-level adapter owned by the concrete engine.
 *
 * The legacy worker path still lets `RealSqliteDatabase` synthesize sync
 * behavior locally, but a sqlite3mt-backed engine needs a way to surface its
 * real sync lifecycle, events, and diagnostics without teaching the public DB
 * layer about control-runtime internals.
 */
export interface SqliteEngineDatabaseAdapter {
    setSyncMode(mode: SyncMode): Promise<void>
    startSync(options?: { interval?: number }): Promise<void>
    stopSync(): Promise<void>
    syncNow(): Promise<SyncResult>
    flushLocal(options?: SqliteFlushLocalOptions): Promise<void>
    flushRemote(): Promise<void>
    onSync(callback: (event: SyncEvent) => void): () => void
     getSyncDiagnosticsSnapshot(): Partial<SyncDiag>
     /** Current database page count (nTruncate after last commit). */
     getPageCount(): number
}

export interface SqliteEngineHandle {
    exec(sql: string, params?: unknown[]): void
    query<T = Record<string, unknown>>(sql: string, params?: unknown[]): T[]
    getDirtyPages(): SqliteDirtyPage[]
    resetDirtyTracking(): void
    exportFullDb(): Uint8Array
    importFullDb(bytes: Uint8Array): void
    exportDirtyPages(): Map<number, Uint8Array>
    importMergedPages(pages: Map<number, Uint8Array>): void
    appendWalFramesForSync(pages: Map<number, Uint8Array>): void
    readPages(pgnos: number[]): Promise<Map<number, Uint8Array>>
    close(): void

    readonly pageSize: number
    readonly vfs: SqliteVfsCompat
    readonly memoryManager: SqliteMemoryManagerCompat
    readonly dbPtr: number
    readonly databaseAdapter?: SqliteEngineDatabaseAdapter
}

export function attachDatabaseAdapter(
    handle: SqliteEngineHandle,
    databaseAdapter: SqliteEngineDatabaseAdapter
): SqliteEngineHandle {
    if (handle.databaseAdapter === databaseAdapter) {
        return handle
    }

    if (handle.databaseAdapter != null) {
        throw new Error('SqliteEngineHandle already has a database adapter attached')
    }

    Object.defineProperty(handle, 'databaseAdapter', {
        value: databaseAdapter,
        enumerable: true,
        configurable: true,
    })

    return handle
}

export interface SqliteEngineOpenConfig {
    workspace: string
    backgroundSync?: 'workspace-policy' | 'manual'
    initialDb?: Uint8Array
    pageCount?: number
    pageSize?: number
    seedPages?: Map<number, Uint8Array>
    onPageMiss?: (pgno: number) => Promise<Uint8Array | null>
    onCommit?: (
        dirtyPages: Map<number, Uint8Array>,
        mode: TransactionMode,
        pageCount: number
    ) => void | Promise<void>
}

export interface SqliteEngineFactory {
    openRuntimeOwner(config: SqliteEngineOpenConfig): Promise<SqliteEngineHandle>
    openClient(runtimeOwner: SqliteEngineHandle): Promise<SqliteEngineHandle>
}

export interface WorkspaceSqliteEngineFactoryContext {
    readonly databaseAdapter: SqliteEngineDatabaseAdapter
    readonly defaultFactory: SqliteEngineFactory
}

/**
 * Configuration options for enabling sqlite3mt multi-worker SQLite.
 *
 * When set on `SdkConfig.useSqlite3Mt`, `RealWorkspace` constructs the
 * sqlite3mt provider lazily from workspace-level deps (repository, crypto,
 * stateKey, blobId) instead of requiring an explicit `createSqliteEngineFactory`
 * callback at SDK creation time.
 *
 * This is the bounded config seam that unblocks the workspace→sqlite3mt
 * integration slice: the build phase will use these options plus the
 * workspace-level deps to call `createSqlite3MtWorkspaceProvider()`.
 */
export interface Sqlite3MtOptions {
    /**
     * Backend implementation. Default: 'sqlite3mt'.
     * 'sqlite3mtlight' routes SqliteDatabase connections to the SecureBlob
     * WebSocket SQLite service instead of the browser/node sqlite3mt runtime.
     */
    backend?: 'sqlite3mt' | 'sqlite3mtlight'
    /** Page size in bytes. Default: 4096. */
    pageSize?: number
    /**
     * Initial WAL shadow bytes allocated in SAB2. The buffer can grow up to
     * `maxWalSize` when the JS runtime supports growable SharedArrayBuffer.
     * Default: min(32 MiB, maxWalSize). Explicit values must be <= maxWalSize.
     */
    initialWalSize?: number
    /**
     * Maximum WAL shadow bytes available in SAB2 for a single SQLite commit.
     * Default: min(128 MiB, configured wal-index/SHM capacity).
     */
    maxWalSize?: number
    /**
     * Bounded read-ahead window for sqlite3mt page faults after sequential
     * demand has been observed. Set 0 to disable. Default: 32 pages.
     */
    faultReadAheadPages?: number
    /**
     * Persistence mode for the durable local store (journal, snapshot, intent).
     * Default: 'idb' (IndexedDB in browser, in-memory otherwise).
     */
    persistence?: 'idb' | 'memory'
    sqlite3MtLight?: Sqlite3MtLightOptions
}

export interface Sqlite3MtLightOptions {
    /**
     * Absolute WebSocket endpoint. Defaults to
     * `${apiBaseUrl}/sqlite3mtlight/ws` with http(s) converted to ws(s).
     */
    websocketUrl?: string
    reconnect?: boolean | Sqlite3MtLightReconnectOptions
    requestTimeoutMs?: number
}

export interface Sqlite3MtLightReconnectOptions {
    initialDelayMs?: number
    maxDelayMs?: number
    jitterRatio?: number
    maxAttempts?: number
}

export interface WorkspaceSqliteEngineFactoryProvider {
    createSqliteEngineFactory?(
        context: WorkspaceSqliteEngineFactoryContext
    ): Promise<SqliteEngineFactory> | SqliteEngineFactory
}

export function toSqliteEngineOpenConfig(
    config: DatabaseConfig
): SqliteEngineOpenConfig {
    return {
        workspace: config.workspace,
        backgroundSync: config.backgroundSync,
        initialDb: config.initialDb,
        pageCount: config.pageCount,
        pageSize: config.pageSize,
        seedPages: config.seedPages,
        onPageMiss: config.onPageMiss,
        onCommit: config.onCommit,
    }
}

/**
 * Create a default engine factory that attaches a database adapter.
 *
 * @deprecated Use sqlite3mt (`useSqlite3Mt` on workspace config) instead.
 */
export function createDefaultWorkspaceSqliteEngineFactory(
    databaseAdapter: SqliteEngineDatabaseAdapter,
    baseFactory: SqliteEngineFactory
): SqliteEngineFactory {
    return {
        async openRuntimeOwner(config) {
            const handle = await baseFactory.openRuntimeOwner(config)
            return attachDatabaseAdapter(handle, databaseAdapter)
        },

        openClient(runtimeOwner) {
            return baseFactory.openClient(runtimeOwner)
        },
    }
}

export async function resolveWorkspaceSqliteEngineFactory(
    workspace: WorkspaceSqliteEngineFactoryProvider,
    options: {
        databaseAdapter?: SqliteEngineDatabaseAdapter
        baseFactory?: SqliteEngineFactory
    } = {}
): Promise<SqliteEngineFactory> {
    if (workspace.createSqliteEngineFactory == null) {
        throw new Error(
            'sqlite3mt requires createSqliteEngineFactory on the workspace. ' +
            'Pass useSqlite3Mt on the SDK config to enable it.'
        )
    }

    return await workspace.createSqliteEngineFactory({
        databaseAdapter: options.databaseAdapter ?? {
            setSyncMode: async () => {},
            startSync: async () => {},
            stopSync: async () => {},
            syncNow: async () => ({ direction: 'no-op' as const, pagesChanged: 0, conflicts: [], durationMs: 0 }),
            flushLocal: async () => {},
            flushRemote: async () => {},
            onSync: () => () => {},
            getSyncDiagnosticsSnapshot: () => ({}),
            getPageCount: () => 0,
        },
        defaultFactory: options.baseFactory ?? {
            openRuntimeOwner: async () => { throw new Error('No base factory — sqlite3mt required') },
            openClient: async () => { throw new Error('No base factory — sqlite3mt required') },
        },
    })
}
